/**
 * The model of the contract-funnel widget (ADDITIVE v1.9.23, T.Z105) — the block
 * under the campaign summary that shows what the T.Z101 синк договоров has said
 * about the campaign: «847 подписали / 357 не подписали / 1382 не пришли».
 *
 * Pure on purpose — the `unit` Playwright project imports it in plain Node, so it
 * may not reach the api client (type-only imports are erased) and every decision a
 * section needs lives here as data: its RU label, its printed count, its drill-down
 * href and its colour. `ContractFunnel.tsx` only renders that list.
 *
 * The hrefs are the register's `?contract=` values (v1.9.20/#3; v1.9.24/T.Z111 adds
 * `enrolled`/`declined`) — the SAME predicates the api counts the buckets with, so
 * the number a section prints is the row count the register answers. Spelled as
 * literals here so this module stays importable from plain Node without pulling the
 * register's view model in.
 */

import type { ContractFunnelDto } from '../../api/dashboard';

import { APPLICATIONS_PATH } from './dashboard-model';
import { formatCount } from './format';
import type { BadgeTone } from './status';

/** The wire vocabulary of #3's `?contract=` — the same order the widget reads them.
 *  ADDITIVE (v1.9.24, T.Z111): the enrollment pair joins it. */
export type ContractFunnelKey = 'signed' | 'not_signed' | 'not_arrived' | 'enrolled' | 'declined';

export interface ContractFunnelSection {
  /** The register's `?contract=` value — also the `data-testid` suffix. */
  key: ContractFunnelKey;
  /** The RU caption, the dashboard's own wording style. */
  label: string;
  /** The printed count, RU-formatted (thin group separators like the cards use). */
  count: string;
  /** Where the click lands — the register, pre-filtered to this bucket. */
  href: string;
  /** Green for the done bucket, red for the two queues that need a person. */
  tone: ContractFunnelTone;
  /** The hover tooltip — why the click is worth it. */
  hint: string;
}

/** Green = the contract is signed (the `badge--ok` pair); red = work pending (`badge--bad`). */
export type ContractFunnelTone = Extract<BadgeTone, 'ok' | 'bad'>;

/**
 * The five sections, in the funnel's own reading order. `funnel` may be absent
 * (the skeleton renders before the fetch settles, or an older api sent no funnel):
 * every count degrades to `0` and the widget still renders — «the sync has not run
 * yet» is information, not a reason to hide the block.
 *
 * ADDITIVE (v1.9.24, T.Z111) — the two ENROLLMENT sections join the same grid, the
 * same real-`<a>` pattern: «Зачислен» (green, the platform's ACCEPTED) and «Отказано
 * в зачислении» (red), each clicking through to the register under its own
 * `?contract=` value. The two new counts degrade to 0 the same way when an older api
 * (v1.9.23 and earlier) omits them.
 *
 * ADDITIVE (T.Z144, v1.9.37) — `format` is the «Тип площадки» axis
 * (`dashboard-model`'s `kindFormat`): it joins every section's URL, because the counts
 * the sections print come off the SAME #11 read narrowed by that `format=` — a section
 * that printed offline-only counts must not land on a format-mixed register.
 */
export function contractFunnelSections(
  funnel: ContractFunnelDto | null | undefined,
  format?: 'OFFLINE' | 'ONLINE',
): ContractFunnelSection[] {
  const signed = normalizeFunnelCount(funnel?.contractSigned);
  const notSigned = normalizeFunnelCount(funnel?.contractNotSigned);
  const notArrived = normalizeFunnelCount(funnel?.contractNotArrived);
  const enrolled = normalizeFunnelCount(funnel?.enrolled);
  const declined = normalizeFunnelCount(funnel?.declined);
  // T.Z144 — the axis parameter, once per widget: absent on «вместе», `&format=…`
  // (the `?contract=` is already there) when a kind filter is on.
  const formatSuffix = format === 'OFFLINE' || format === 'ONLINE' ? `&format=${format}` : '';
  return [
    {
      key: 'signed',
      label: 'Подписали договор',
      count: formatCount(signed),
      href: `${APPLICATIONS_PATH}?contract=signed${formatSuffix}`,
      tone: 'ok',
      hint: `Открыть заявления: договор подписан (${formatCount(signed)})`,
    },
    {
      key: 'not_signed',
      label: 'Не подписали',
      count: formatCount(notSigned),
      href: `${APPLICATIONS_PATH}?contract=not_signed${formatSuffix}`,
      tone: 'bad',
      hint: `Открыть заявления: пришёл в iSmart, договор не подписан (${formatCount(notSigned)})`,
    },
    {
      key: 'not_arrived',
      label: 'Не пришли в iSmart',
      count: formatCount(notArrived),
      href: `${APPLICATIONS_PATH}?contract=not_arrived${formatSuffix}`,
      tone: 'bad',
      hint: `Открыть заявления: не пришёл в iSmart — обзвон (${formatCount(notArrived)})`,
    },
    {
      key: 'enrolled',
      label: 'Зачислены',
      count: formatCount(enrolled),
      href: `${APPLICATIONS_PATH}?contract=enrolled${formatSuffix}`,
      tone: 'ok',
      hint: `Открыть заявления: зачислены на платформе iSmart (${formatCount(enrolled)})`,
    },
    {
      key: 'declined',
      label: 'Отказано в зачислении',
      count: formatCount(declined),
      href: `${APPLICATIONS_PATH}?contract=declined${formatSuffix}`,
      tone: 'bad',
      hint: `Открыть заявления: отказано в зачислении на платформе iSmart (${formatCount(declined)})`,
    },
  ];
}

/** A count that is not a sane count is `0` — the api serialises all three keys. */
function normalizeFunnelCount(count: number | null | undefined): number {
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) return 0;
  return Math.round(count);
}
